import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faSearch, 
  faFilter, 
  faDownload, 
  faEye,
  faEdit,
  faTrash,
  faPaperPlane,
  faCalendarAlt,
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faEllipsisV,
  faRefresh,
  faUpload,
  faChartBar,
  faSort,
  faSortUp,
  faSortDown,
  faTimesCircle,
  faSpinner,
  faFileExcel,
  faFilePdf,
  faTimes,
  faClipboardList,
  faBell,
  faCheck,
  faEnvelope,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { 
  getSubmissions, 
  getSubmissionStats, 
  getDeadlineStats,
  exportSubmissions,
  bulkSendQuestionnaires,
  updateSubmissionStatus,
  addInternalNote,
  deleteSubmission,
  sendManualReminder,
  bulkDeleteSubmissions
} from '../services/submissionApi';
import { getQuestionnaireTemplates, getSuppliers } from '../services/api';

import { Submission, SubmissionFilters, SubmissionStats, DeadlineStats } from '../types/submission';
import { useToast } from '../components/ui/ToastProvider';
import BulkSendModal from '../components/submissions/BulkSendModal';
import SupplierMultiSelect from '../components/ui/SupplierMultiSelect';
import SubmissionDetailModal from '../components/submissions/SubmissionDetailModal';
import DeadlineUpdateModal from '../components/submissions/DeadlineUpdateModal';
import ReminderWarningModal from '../components/submissions/ReminderWarningModal';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import SubmissionAnswersModal from '../components/submissions/SubmissionAnswersModal';

type SubAccent = 'blue' | 'amber' | 'violet' | 'emerald' | 'red' | 'orange';

const STAT_ACCENT: Record<SubAccent, { icon: string }> = {
  blue:    { icon: 'bg-blue-100 text-blue-600' },
  amber:   { icon: 'bg-amber-100 text-amber-600' },
  violet:  { icon: 'bg-violet-100 text-violet-600' },
  emerald: { icon: 'bg-emerald-100 text-emerald-600' },
  red:     { icon: 'bg-red-100 text-red-600' },
  orange:  { icon: 'bg-orange-100 text-orange-600' },
};

const StyledSelect: React.FC<React.SelectHTMLAttributes<HTMLSelectElement> & { children: React.ReactNode }> = ({ className = '', children, ...props }) => (
  <div className="relative">
    <select
      {...props}
      className={`appearance-none pl-3 pr-9 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-400 transition-all cursor-pointer ${className}`}
    >
      {children}
    </select>
    <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  </div>
);

const Submissions: React.FC = () => {
  // **HOOKS**
  const { showSuccess, showWarning, showError } = useToast();
  
  // **STATE MANAGEMENT**
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // **STATISTICS**
  const [stats, setStats] = useState<SubmissionStats>({
    total: 0,
    sent: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0,
    notSentThisYear: 0,
    remindersDue: 0,
    completionRate: 0,
    averageCompletionTime: 0
  });
  
  const [deadlineStats, setDeadlineStats] = useState<DeadlineStats>({
    dueToday: 0,
    dueTomorrow: 0,
    dueThisWeek: 0,
    dueNextWeek: 0,
    overdue: 0
  });

  // **PAGINATION & FILTERS**
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  
  const [filters, setFilters] = useState<SubmissionFilters>({
    search: '',
    status: 'all',
    assignedTo: 'all',
    templateId: 'all',
    supplierId: [],
    yearGroup: new Date().getFullYear(),
    overdue: false,
    deadlineFrom: '',
    deadlineTo: '',
    showDeleted: false
  });

  // **SORTING**
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // **MODALS**
  const [showBulkSendModal, setShowBulkSendModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
  const [showDeadlineModal, setShowDeadlineModal] = useState(false);
  const [showReminderWarningModal, setShowReminderWarningModal] = useState(false);
  const [reminderWarningData, setReminderWarningData] = useState<{
    submission: Submission;
    daysSinceLastReminder: number;
    lastReminderDate: Date;
  } | null>(null);
  const [deadlineModalData, setDeadlineModalData] = useState<{
    submissionId: string;
    currentDeadline: string;
    supplierName: string;
  } | null>(null);

  // **NEUE DELETE MODAL STATES**
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalData, setDeleteModalData] = useState<{
    type: 'single' | 'bulk';
    submissionId?: string;
    supplierName?: string;
    count?: number;
  } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // **SELECTION**
  const [selectedSubmissions, setSelectedSubmissions] = useState<string[]>([]);

  // **GRUPPIERUNG: State für ein-/ausgeklappte Gruppen (standardmäßig eingeklappt)**

  // **ACTIONS**
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [bulkPDFLoading, setBulkPDFLoading] = useState(false);
  const [bulkPDFProgress, setBulkPDFProgress] = useState<{ current: number; total: number } | null>(null);

  const [showAnswersModal, setShowAnswersModal] = useState(false);
  const [selectedSubmissionForAnswers, setSelectedSubmissionForAnswers] = useState<string | null>(null);

  // **DEBOUNCED SEARCH STATE**
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [wasSearchFocused, setWasSearchFocused] = useState(false);

  // **LOAD DATA - getrennte Funktionen für bessere Performance**
  const loadSubmissions = useCallback(async (customSearch?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const searchTerm = customSearch !== undefined ? customSearch : debouncedSearch;
      const effectiveFilters = { ...filters, search: searchTerm };
      
      const submissionsResponse = await getSubmissions({
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
        ...effectiveFilters,
        // Nur senden wenn Suppliers ausgewählt sind
        supplierId: Array.isArray(effectiveFilters.supplierId) && effectiveFilters.supplierId.length > 0 
          ? effectiveFilters.supplierId 
          : 'all'
      });

      setSubmissions(submissionsResponse.data.submissions || []);
      setTotalPages(submissionsResponse.data.pagination?.pages || 0);
      setTotalItems(submissionsResponse.data.pagination?.total || 0);
    } catch (err) {
      console.error('Error loading submissions:', err);
      setError('Fehler beim Laden der Submissions');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, filters, sortBy, sortOrder, debouncedSearch]);

  const loadStaticData = useCallback(async () => {
    try {
      // **OPTIMIZED: Load only essential data for filters**
      const [templatesResponse, suppliersResponse] = await Promise.all([
        getQuestionnaireTemplates(),
        getSuppliers({ 
          limit: 50, // Reduziert von 1000 auf 50 für bessere Performance
          sortBy: 'name',
          sortOrder: 'asc',
          isActive: 'true' // Nur aktive Suppliers für Filter
        })
      ]);
      
      setTemplates(templatesResponse.data || []);
      setSuppliers(suppliersResponse.data.suppliers || []);
    } catch (err) {
      console.error('Error loading static data:', err);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const [statsResponse, deadlineStatsResponse] = await Promise.all([
        getSubmissionStats(filters.yearGroup),
        getDeadlineStats()
      ]);
      
      setStats(statsResponse.data || stats);
      setDeadlineStats(deadlineStatsResponse.data || deadlineStats);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, [filters.yearGroup]);

  // **COMBINED LOAD FUNCTION - Only for initial load**
  const loadInitialData = useCallback(async () => {
    await Promise.all([
      loadSubmissions(),
      loadStaticData(),
      loadStats()
    ]);
  }, [loadSubmissions, loadStaticData, loadStats]);

  // **REFRESH FUNCTION - Only reload submissions and stats, NOT suppliers**
  const refreshSubmissions = useCallback(async () => {
    await Promise.all([
      loadSubmissions(),
      loadStats()
    ]);
  }, [loadSubmissions, loadStats]);

  // **INITIAL LOAD - Nur einmal beim Mount**
  useEffect(() => {
    loadInitialData(); // Lädt alles beim ersten Mal
  }, [loadInitialData]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [filters.search]);

  // **SUBMISSIONS RELOAD - bei allen Filter/Sortierung/Pagination Änderungen**
  useEffect(() => {
    loadSubmissions(debouncedSearch);
  }, [currentPage, itemsPerPage, sortBy, sortOrder, filters.status, filters.supplierId, filters.templateId, filters.yearGroup, filters.showDeleted, debouncedSearch]);

  // **RESTORE FOCUS AFTER LOADING**
  useEffect(() => {
    if (!loading && wasSearchFocused && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [loading, wasSearchFocused]);

  // **FILTER HANDLERS**
  const handleFilterChange = (key: keyof SubmissionFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
    setSelectedSubmissions([]);
  };

  // **STABILIZED SEARCH HANDLERS**
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
    setCurrentPage(1);
    setSelectedSubmissions([]);
  }, []);

  const handleSearchFocus = useCallback(() => {
    setWasSearchFocused(true);
  }, []);

  const handleSearchBlur = useCallback(() => {
    setWasSearchFocused(false);
  }, []);

  const handleSupplierFilterChange = (supplierIds: string[]) => {
    setFilters(prev => ({ ...prev, supplierId: supplierIds }));
    setCurrentPage(1);
    setSelectedSubmissions([]);
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  // **GRUPPIERUNG: Toggle-Funktion für Gruppen (standardmäßig eingeklappt)**
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  
  const toggleGroupCollapse = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      assignedTo: 'all',
      templateId: 'all',
      supplierId: [],
      yearGroup: new Date().getFullYear(),
      overdue: false,
      deadlineFrom: '',
      deadlineTo: '',
      showDeleted: false
    });
    setCurrentPage(1);
    setSelectedSubmissions([]);
  };

  // **SELECTION HANDLERS** 
  const handleSelectSubmission = (submissionId: string) => {
    setSelectedSubmissions(prev => 
      prev.includes(submissionId)
        ? prev.filter(id => id !== submissionId)
        : [...prev, submissionId]
    );
  };

  const handleSelectAll = () => {
    // Filter out group headers - only select actual submissions
    const allIds = actualSubmissions.map(sub => sub._id);
    setSelectedSubmissions(prev => 
      prev.length === allIds.length ? [] : allIds
    );
  };

  // **BULK ACTIONS**
  const handleBulkStatusUpdate = async (newStatus: string) => {
    if (selectedSubmissions.length === 0) return;
    
    setBulkActionLoading(true);
    try {
      await Promise.all(
        selectedSubmissions.map(id => 
          updateSubmissionStatus(id, newStatus, 'Bulk-Update')
        )
      );
      setSelectedSubmissions([]);
      refreshSubmissions();
    } catch (error) {
      console.error('Error updating bulk status:', error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedSubmissions.length === 0) return;
    
    setDeleteModalData({
      type: 'bulk',
      count: selectedSubmissions.length
    });
    setShowDeleteModal(true);
  };

  const handleBulkReminder = async () => {
    if (selectedSubmissions.length === 0) return;
    
    setBulkActionLoading(true);
    try {
      await Promise.all(
        selectedSubmissions.map(id => sendManualReminder(id))
      );
      setSelectedSubmissions([]);
      refreshSubmissions();
    } catch (error) {
      console.error('Error sending reminders:', error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // **BULK PDF DOWNLOAD AS ZIP — client-side html2canvas (same design as HTML export)**
  const handleBulkPDFDownload = async (ids?: string[]) => {
    const targetIds = ids ?? (selectedSubmissions.length > 0
      ? selectedSubmissions
      : actualSubmissions.map(s => s._id));

    if (targetIds.length === 0) return;

    setBulkPDFLoading(true);
    setBulkPDFProgress({ current: 0, total: targetIds.length });

    try {
      const JSZip = (await import('jszip')).default;
      const { getSubmissionAnswers } = await import('../services/submissionApi');
      const { buildSubmissionPdfBlob } = await import('../utils/submissionPdfGenerator');

      const zip = new JSZip();
      const sanitize = (str: string) => str.replace(/[/\\:*?"<>|]/g, '_').trim();

      // Fetch all data in parallel batches of 20
      const BATCH_SIZE = 20;
      let completed = 0;
      let errors = 0;

      for (let i = 0; i < targetIds.length; i += BATCH_SIZE) {
        const batch = targetIds.slice(i, i + BATCH_SIZE);

        const fetched = await Promise.allSettled(
          batch.map(id => getSubmissionAnswers(id).then(r => ({ id, data: r.data })))
        );

        // Generate PDFs sequentially (html2canvas needs DOM)
        for (const result of fetched) {
          completed++;
          setBulkPDFProgress({ current: completed, total: targetIds.length });

          if (result.status === 'rejected') { errors++; continue; }

          try {
            const { data } = result.value;
            const payload = (data as any).success ? (data as any).data : data;
            const { submission, supplier, template, answeredQuestions, totalQuestions } = payload;

            const kreditorNumber    = sanitize(supplier?.kreditorNumber || '');
            const supplierName      = sanitize(supplier?.name || 'Unbekannt');
            const questionnaireName = sanitize(template?.title?.de || 'Fragebogen');
            const filePrefix = kreditorNumber
              ? `${kreditorNumber}_${supplierName}_${questionnaireName}`
              : `${supplierName}_${questionnaireName}`;

            const blob = await buildSubmissionPdfBlob({ submission, supplier, template, answeredQuestions, totalQuestions });
            zip.folder(questionnaireName)!.file(`${filePrefix}.pdf`, blob);
          } catch (e) {
            errors++;
          }
        }
      }

      if (errors === targetIds.length) {
        showError('PDF-Export fehlgeschlagen', 'Es konnte kein PDF erstellt werden.');
        return;
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fragebogen_antworten_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      if (errors > 0) {
        showWarning('Einige PDFs fehlgeschlagen', `${errors} von ${targetIds.length} PDFs konnten nicht erstellt werden.`);
      } else {
        showSuccess('PDF-Export erfolgreich', `${targetIds.length} PDFs wurden als ZIP-Datei heruntergeladen.`);
      }
    } catch (error: any) {
      console.error('Bulk PDF download error:', error);
      showError('Fehler beim PDF-Export', error.message);
    } finally {
      setBulkPDFLoading(false);
      setBulkPDFProgress(null);
    }
  };

  // **SINGLE REMINDER WITH TOAST AND WARNING**
  const handleSingleReminder = async (submission: Submission) => {
    try {
      // **CHECK IF REMINDER WAS SENT WITHIN LAST WEEK**
      const lastReminderDate = submission.lastReminderAt ? new Date(submission.lastReminderAt) : null;
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
      
      if (lastReminderDate && lastReminderDate > oneWeekAgo) {
        const daysSinceLastReminder = Math.ceil((new Date().getTime() - lastReminderDate.getTime()) / (1000 * 3600 * 24));
        
        // **SHOW CUSTOM MODAL INSTEAD OF WINDOW.CONFIRM**
        setReminderWarningData({
          submission,
          daysSinceLastReminder,
          lastReminderDate
        });
        setShowReminderWarningModal(true);
        return;
      }
      
      // **SEND REMINDER DIRECTLY IF NO WARNING NEEDED**
      await sendReminderDirectly(submission);
      
    } catch (error) {
      console.error('Error sending reminder:', error);
      showError(
        'Fehler beim Senden der Erinnerung',
        'Die Erinnerung konnte nicht gesendet werden. Bitte versuchen Sie es erneut.'
      );
    }
  };

  // **HELPER FUNCTION TO SEND REMINDER**
  const sendReminderDirectly = async (submission: Submission) => {
    try {
      await sendManualReminder(submission._id);
      
      // **SHOW SUCCESS TOAST**
      showSuccess(
        'Erinnerung gesendet',
        `Erinnerung wurde erfolgreich an ${submission.supplierId.name} gesendet.`
      );
      
      // **RELOAD DATA TO UPDATE lastReminderAt**
      refreshSubmissions();
      
    } catch (error) {
      console.error('Error sending reminder:', error);
      showError(
        'Fehler beim Senden der Erinnerung',
        'Die Erinnerung konnte nicht gesendet werden. Bitte versuchen Sie es erneut.'
      );
    }
  };

  // **HANDLE CONFIRMED REMINDER FROM MODAL**
  const handleConfirmedReminder = () => {
    if (reminderWarningData) {
      sendReminderDirectly(reminderWarningData.submission);
      setReminderWarningData(null);
    }
  };

  // **SINGLE DELETE**
  const handleDeleteSubmission = async (submissionId: string, supplierName: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    setDeleteModalData({
      type: 'single',
      submissionId,
      supplierName
    });
    setShowDeleteModal(true);
  };

  // **NEUE DELETE CONFIRM FUNKTION**
  const handleDeleteConfirm = async () => {
    if (!deleteModalData) return;

    try {
      setDeleteLoading(true);
      
      if (deleteModalData.type === 'bulk') {
        await Promise.all(
          selectedSubmissions.map(id => deleteSubmission(id))
        );
        setSelectedSubmissions([]);
      } else if (deleteModalData.type === 'single' && deleteModalData.submissionId) {
        await deleteSubmission(deleteModalData.submissionId);
      }
      
      // Nach dem Löschen: Submissions und Stats neu laden
      await Promise.all([
        loadSubmissions(),
        loadStats()
      ]);
      setShowDeleteModal(false);
      setDeleteModalData(null);
      
    } catch (error) {
      console.error('Error deleting submission(s):', error);
      setError('Fehler beim Löschen der Submission(s)');
    } finally {
      setDeleteLoading(false);
    }
  };

  // **DELETE MODAL SCHLIESSEN**
  const handleCloseDeleteModal = () => {
    if (deleteLoading) return; // Prevent closing while deleting
    setShowDeleteModal(false);
    setDeleteModalData(null);
  };

  // **DELETE MODAL TITLE UND MESSAGE**
  const getDeleteModalContent = () => {
    if (!deleteModalData) return { title: '', message: '' };

    if (deleteModalData.type === 'bulk') {
      return {
        title: 'Submissions löschen',
        message: `Möchten Sie ${deleteModalData.count} Submission${deleteModalData.count === 1 ? '' : 's'} wirklich löschen?`
      };
    } else {
      return {
        title: 'Submission löschen',
        message: `Möchten Sie die Submission für "${deleteModalData.supplierName}" wirklich löschen?`
      };
    }
  };

  // **EXPORT**
  const handleExport = async (format: 'xlsx' | 'pdf') => {
    setExportLoading(true);
    try {
      const response = await exportSubmissions(format, filters);
      
      const blob = new Blob([response.data], {
        type: format === 'xlsx' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `submissions-${filters.yearGroup}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting:', error);
      setError('Fehler beim Exportieren');
    } finally {
      setExportLoading(false);
    }
  };

  // **MODAL HANDLERS**
  const handleShowDetail = (submissionId: string) => {
    setSelectedSubmissionId(submissionId);
    setShowDetailModal(true);
  };

  const handleShowDeadlineModal = (submission: Submission) => {
    setDeadlineModalData({
      submissionId: submission._id,
      currentDeadline: submission.deadline,
      supplierName: submission.supplierId?.name || 'Unbekannt'
    });
    setShowDeadlineModal(true);
  };

  const handleViewAnswers = useCallback((submission: Submission, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedSubmissionForAnswers(submission._id);
    setShowAnswersModal(true);
  }, []);

  const handleCloseAnswersModal = useCallback(() => {
    setShowAnswersModal(false);
    setSelectedSubmissionForAnswers(null);
  }, []);

  // **HELPER: CAN SHOW ANSWERS**
  const canShowAnswers = useCallback((submission: Submission) => {
    return submission.status === 'in_progress' || 
           submission.status === 'completed' || 
           (submission.progressPercentage != null && submission.progressPercentage > 0);
  }, []);

  // **COMPUTED VALUES**
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const getDaysUntilDeadline = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      completed:   { label: 'Abgeschlossen', class: 'bg-emerald-50 text-emerald-700 border-emerald-100', dot: 'bg-emerald-500' },
      in_progress: { label: 'In Bearbeitung', class: 'bg-blue-50 text-blue-700 border-blue-100', dot: 'bg-blue-500' },
      sent:        { label: 'Versendet', class: 'bg-amber-50 text-amber-700 border-amber-100', dot: 'bg-amber-500' },
      overdue:     { label: 'Überfällig', class: 'bg-red-50 text-red-700 border-red-100', dot: 'bg-red-500' },
      draft:       { label: 'Entwurf', class: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' }
    };
    const badge = badges[status as keyof typeof badges] || badges.draft;
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-full font-medium border ${badge.class}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`}></span>
        {badge.label}
      </span>
    );
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return <FontAwesomeIcon icon={faSort} className="text-gray-400" />;
    return <FontAwesomeIcon icon={sortOrder === 'asc' ? faSortUp : faSortDown} className="text-slate-600" />;
  };

  if (loading) {
    return <LoadingSpinner size="medium" text="Submissions werden geladen..." />;
  }

  // Helper: Get actual submissions (without group headers) for selection logic
  const actualSubmissions = submissions.filter(sub => !sub.isGroupHeader);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* **TOOLBAR** */}
      <div className="bg-white border-b border-slate-200/70">
        <div className="max-w-[1700px] mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-wrap items-center gap-2">
            {/* **SUCHE** */}
            <div className="relative flex-1 min-w-[240px]">
              <FontAwesomeIcon icon={faSearch} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Submissions durchsuchen..."
                value={filters.search}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                className="w-full pl-10 pr-9 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-400 bg-white text-sm transition-all"
              />
              {filters.search && (
                <button
                  onClick={() => handleFilterChange('search', '')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <FontAwesomeIcon icon={faTimes} className="text-xs" />
                </button>
              )}
            </div>

            {/* **FILTER UND AKTIONEN** */}
            <StyledSelect value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)} className="min-w-[130px]">
                <option value="all">Alle Status</option>
                <option value="draft">Entwurf</option>
                <option value="sent">Versendet</option>
                <option value="in_progress">In Bearbeitung</option>
                <option value="completed">Abgeschlossen</option>
                <option value="overdue">Überfällig</option>
                <option value="expired">Abgelaufen</option>
              </StyledSelect>

              <StyledSelect value={filters.showDeleted ? 'deleted' : 'active'} onChange={(e) => handleFilterChange('showDeleted', e.target.value === 'deleted')} className="min-w-[110px]">
                <option value="active">Aktiv</option>
                <option value="deleted">Gelöscht</option>
              </StyledSelect>

              <StyledSelect value={filters.templateId} onChange={(e) => handleFilterChange('templateId', e.target.value)} className="min-w-[150px]">
                <option value="all">Alle Templates</option>
                {templates.map(template => (
                  <option key={template._id} value={template._id}>
                    {template.title.de || template.title.en || 'Untitled'}
                  </option>
                ))}
              </StyledSelect>

              <SupplierMultiSelect
                suppliers={suppliers}
                selectedSupplierIds={Array.isArray(filters.supplierId) ? filters.supplierId : []}
                onChange={handleSupplierFilterChange}
                placeholder="Alle Lieferanten"
                className="min-w-[180px]"
              />

              <StyledSelect value={filters.yearGroup} onChange={(e) => handleFilterChange('yearGroup', parseInt(e.target.value))} className="min-w-[90px]">
                {Array.from({length: 5}, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </StyledSelect>

              <button
                onClick={resetFilters}
                className="w-9 h-9 flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
                title="Filter zurücksetzen"
              >
                <FontAwesomeIcon icon={faTimesCircle} />
              </button>

              <div className="h-6 w-px bg-slate-200 mx-1"></div>

              <button
                onClick={() => handleExport('xlsx')}
                disabled={exportLoading}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-emerald-700 rounded-xl hover:border-emerald-300 hover:bg-emerald-50 transition-colors text-sm font-medium disabled:opacity-50"
              >
                <FontAwesomeIcon icon={exportLoading ? faSpinner : faFileExcel} className={`text-xs ${exportLoading ? 'animate-spin' : ''}`} />
                Export
              </button>

              <button
                onClick={() => handleBulkPDFDownload()}
                disabled={bulkPDFLoading}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-indigo-700 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-sm font-medium disabled:opacity-50"
                title={selectedSubmissions.length > 0 ? `${selectedSubmissions.length} ausgewählte als PDF ZIP` : 'Alle angezeigten als PDF ZIP'}
              >
                <FontAwesomeIcon icon={bulkPDFLoading ? faSpinner : faFilePdf} className={`text-xs ${bulkPDFLoading ? 'animate-spin' : ''}`} />
                {bulkPDFProgress
                  ? `${bulkPDFProgress.current}/${bulkPDFProgress.total}…`
                  : 'PDF ZIP'}
              </button>

              <button
                onClick={() => setShowBulkSendModal(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-br from-[#e2313a] to-[#c9252d] text-white rounded-xl hover:brightness-105 transition-all text-sm font-semibold shadow-sm"
              >
                <FontAwesomeIcon icon={faPaperPlane} className="text-xs" />
                Bulk-Versendung
              </button>
          </div>
        </div>
      </div>

      {/* **ERROR MESSAGE** */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.15 }}
        className="max-w-[1700px] mx-auto px-4 sm:px-6 pt-4"
      >
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-xl flex items-center justify-between text-sm">
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              <FontAwesomeIcon icon={faTimes} className="text-xs" />
            </button>
          </div>
        )}
      </motion.div>

      {/* **STATISTICS CARDS** */}
      <div className="max-w-[1700px] mx-auto px-4 sm:px-6 pt-5 pb-2">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {([
            { label: 'Gesamt', value: stats.total, icon: faClipboardList, color: 'blue' as SubAccent, status: 'all' },
            { label: 'Versendet', value: stats.sent, icon: faPaperPlane, color: 'amber' as SubAccent, status: 'sent' },
            { label: 'In Bearbeitung', value: stats.inProgress, icon: faEdit, color: 'violet' as SubAccent, status: 'in_progress' },
            { label: 'Abgeschlossen', value: stats.completed, icon: faCheckCircle, color: 'emerald' as SubAccent, status: 'completed' },
            { label: 'Überfällig', value: stats.overdue, icon: faExclamationTriangle, color: 'red' as SubAccent, status: 'overdue' },
            { label: 'Erinnerungen', value: stats.remindersDue, icon: faBell, color: 'orange' as SubAccent, status: null as string | null },
          ]).map((card) => {
            const a = STAT_ACCENT[card.color];
            const clickable = card.status !== null;
            const isActive = clickable && filters.status === card.status;
            return (
              <button
                key={card.label}
                type="button"
                onClick={clickable ? () => handleFilterChange('status', card.status as string) : undefined}
                disabled={!clickable}
                className={`text-left w-full bg-white p-4 rounded-2xl border shadow-sm transition-all ${
                  clickable ? 'hover:shadow-md hover:border-slate-300 cursor-pointer' : 'cursor-default'
                } ${isActive ? 'border-red-300 ring-2 ring-red-500/20' : 'border-slate-200/70'}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-500 font-medium">{card.label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{card.value}</p>
                  </div>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${a.icon}`}>
                    <FontAwesomeIcon icon={card.icon} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* **VERBESSERTE BULK ACTIONS** */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="max-w-[1700px] mx-auto px-4 sm:px-6 mb-2"
      >
        {selectedSubmissions.length > 0 && (
          <div className="bg-white border border-slate-200/70 rounded-2xl p-2.5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-gradient-to-br from-[#e2313a] to-[#c9252d] text-white rounded-full flex items-center justify-center text-xs font-bold">
                    {selectedSubmissions.length}
                  </div>
                  <span className="text-sm font-semibold text-slate-800">
                    {selectedSubmissions.length === 1 ? 'Eintrag ausgewählt' : 'Einträge ausgewählt'}
                  </span>
                </div>
                
                <div className="h-4 w-px bg-slate-200"></div>
                
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={handleBulkStatusUpdate.bind(null, 'completed')}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center px-2.5 py-1.5 bg-emerald-500 text-white rounded-lg text-xs font-medium hover:bg-emerald-600 disabled:opacity-50 transition-all duration-150 shadow-sm"
                  >
                    <FontAwesomeIcon icon={faCheck} className="mr-1" />
                    Abschließen
                  </button>

                  <button
                    onClick={() => handleBulkPDFDownload(selectedSubmissions)}
                    disabled={bulkPDFLoading || bulkActionLoading}
                    className="inline-flex items-center px-2.5 py-1.5 bg-indigo-500 text-white rounded-lg text-xs font-medium hover:bg-indigo-600 disabled:opacity-50 transition-all duration-150 shadow-sm"
                    title="PDF-Export als ZIP"
                  >
                    {bulkPDFLoading
                      ? <FontAwesomeIcon icon={faSpinner} spin className="mr-1" />
                      : <FontAwesomeIcon icon={faFilePdf} className="mr-1" />}
                    {bulkPDFProgress
                      ? `${bulkPDFProgress.current}/${bulkPDFProgress.total} PDFs…`
                      : 'PDF ZIP'}
                  </button>
                  
                  <button
                    onClick={handleBulkReminder}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center px-2.5 py-1.5 bg-blue-500 text-white rounded-lg text-xs font-medium hover:bg-blue-600 disabled:opacity-50 transition-all duration-150 shadow-sm"
                  >
                    <FontAwesomeIcon icon={faEnvelope} className="mr-1" />
                    Erinnerung
                  </button>
                  
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    className="inline-flex items-center px-2.5 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 disabled:opacity-50 transition-all duration-150 shadow-sm"
                  >
                    <FontAwesomeIcon icon={faTrash} className="mr-1" />
                    Löschen
                  </button>
                </div>
              </div>
              
              <button
                onClick={() => setSelectedSubmissions([])}
                className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full p-1.5 transition-all duration-150"
                title="Auswahl aufheben"
              >
                <FontAwesomeIcon icon={faTimes} className="text-sm" />
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* **CONTENT BEREICH - LISTE MIT RUNDEN CHECKBOXEN** */}
      <div className="max-w-[1700px] mx-auto px-4 sm:px-6 pb-4">
        {submissions.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200/70 shadow-sm">
            <div className="mb-4">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 text-2xl">
                <FontAwesomeIcon icon={faClipboardList} />
              </div>
              <h3 className="text-base font-semibold text-slate-900 mb-1">
                {filters.search || filters.status !== 'all' 
                  ? 'Keine Submissions gefunden' 
                  : 'Noch keine Submissions vorhanden'}
              </h3>
              <p className="text-slate-500 text-sm max-w-md mx-auto">
                {filters.search || filters.status !== 'all'
                  ? 'Versuchen Sie andere Suchkriterien oder Filter'
                  : 'Erstellen Sie Ihre erste Submission über die Bulk-Versendung'}
              </p>
            </div>
            {!filters.search && filters.status === 'all' && (
              <button
                onClick={() => setShowBulkSendModal(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-br from-[#e2313a] to-[#c9252d] text-white rounded-xl hover:brightness-105 transition-all text-sm font-semibold"
              >
                <FontAwesomeIcon icon={faPaperPlane} className="text-xs" />
                Fragebogen versenden
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/70 overflow-hidden">
            {/* **HORIZONTAL SCROLLBAR AUF KLEINEN BILDSCHIRMEN - DESKTOP-LAYOUT BLEIBT ERHALTEN** */}
            <div className="overflow-x-auto">
            <div className="min-w-[980px]">
            {/* **TABLE HEADER - FLEXIBLES GRID FÜR SAUBERE SPALTENBREITEN** */}
            <div className="bg-slate-50/80 border-b border-slate-200/70 px-4 py-3">
              <div className="grid grid-cols-[40px_minmax(0,1fr)_184px] gap-3 items-center text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                <div className="flex justify-start">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={selectedSubmissions.length === actualSubmissions.length && actualSubmissions.length > 0}
                      onChange={handleSelectAll}
                      className="sr-only"
                      id="select-all"
                    />
                    <label
                      htmlFor="select-all"
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center cursor-pointer transition-all duration-200 ${
                        selectedSubmissions.length === actualSubmissions.length && actualSubmissions.length > 0
                          ? 'bg-red-500 border-red-500'
                          : 'border-slate-300 hover:border-red-400'
                      }`}
                    >
                      {selectedSubmissions.length === actualSubmissions.length && actualSubmissions.length > 0 && (
                        <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                      )}
                    </label>
                  </div>
                </div>
                <div className="grid grid-cols-[minmax(150px,2.2fr)_minmax(140px,1.9fr)_minmax(104px,1.1fr)_minmax(128px,1.3fr)_minmax(150px,1.7fr)] gap-4 items-center">
                  <button
                    onClick={() => handleSort('supplierId.name')}
                    className="flex items-center gap-1 hover:text-slate-900 uppercase tracking-wider"
                  >
                    Lieferant {getSortIcon('supplierId.name')}
                  </button>
                  <button
                    onClick={() => handleSort('questionnaireId.title.de')}
                    className="flex items-center gap-1 hover:text-slate-900 uppercase tracking-wider"
                  >
                    Template {getSortIcon('questionnaireId.title.de')}
                  </button>
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center gap-1 hover:text-slate-900 uppercase tracking-wider"
                  >
                    Status {getSortIcon('status')}
                  </button>
                  <button
                    onClick={() => handleSort('deadline')}
                    className="flex items-center gap-1 hover:text-slate-900 uppercase tracking-wider"
                  >
                    Deadline {getSortIcon('deadline')}
                  </button>
                  <button
                    onClick={() => handleSort('progressPercentage')}
                    className="flex items-center gap-1 hover:text-slate-900 uppercase tracking-wider"
                  >
                    Fortschritt {getSortIcon('progressPercentage')}
                  </button>
                </div>
                <div className="text-right pr-1">
                  Aktionen
                </div>
              </div>
            </div>

            {/* **TABLE BODY MIT LINKSGEBÜNDETEM LAYOUT UND GRUPPIERUNG** */}
            <div className="divide-y divide-slate-100">
              {submissions.map((submission, index) => {
                // Handle group headers
                if (submission.isGroupHeader) {
                  const isExpanded = expandedGroups.has(submission._id);
                  const stats = submission.groupStats || { total: 0, completed: 0, overdue: 0, inProgress: 0, sent: 0 };
                  
                  return (
                    <div
                      key={submission._id}
                      className="bg-gradient-to-r from-slate-50 to-white px-4 py-3 border-l-[3px] border-indigo-400 cursor-pointer hover:from-slate-100 transition-colors"
                      onClick={() => toggleGroupCollapse(submission._id)}
                    >
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-6 h-6 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                            <FontAwesomeIcon 
                              icon={isExpanded ? faChevronDown : faChevronRight} 
                              className="text-indigo-600 w-3 h-3" 
                            />
                          </div>
                          <FontAwesomeIcon icon={faClipboardList} className="text-indigo-500 shrink-0" />
                          <span className="font-semibold text-slate-800 truncate">
                            {submission.groupName}
                          </span>
                          <span className="shrink-0 bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 rounded-full text-xs font-medium">
                            {submission.submissionCount} {submission.submissionCount === 1 ? 'Fragebogen' : 'Fragebögen'}
                          </span>
                        </div>
                        
                        <div className="flex items-center gap-2 shrink-0">
                          {/* Status-Statistiken */}
                          <div className="hidden sm:flex items-center gap-1.5 text-xs">
                            {stats.completed > 0 && (
                              <span className="bg-emerald-50 text-emerald-700 border border-emerald-100 px-2 py-1 rounded-full flex items-center gap-1 font-medium">
                                <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3" />
                                {stats.completed} fertig
                              </span>
                            )}
                            {stats.overdue > 0 && (
                              <span className="bg-red-50 text-red-700 border border-red-100 px-2 py-1 rounded-full flex items-center gap-1 font-medium">
                                <FontAwesomeIcon icon={faExclamationTriangle} className="w-3 h-3" />
                                {stats.overdue} überfällig
                              </span>
                            )}
                            {stats.inProgress > 0 && (
                              <span className="bg-amber-50 text-amber-700 border border-amber-100 px-2 py-1 rounded-full flex items-center gap-1 font-medium">
                                <FontAwesomeIcon icon={faClock} className="w-3 h-3" />
                                {stats.inProgress} in Bearbeitung
                              </span>
                            )}
                            {stats.sent > 0 && stats.overdue === 0 && stats.completed === 0 && stats.inProgress === 0 && (
                              <span className="bg-blue-50 text-blue-700 border border-blue-100 px-2 py-1 rounded-full flex items-center gap-1 font-medium">
                                <FontAwesomeIcon icon={faPaperPlane} className="w-3 h-3" />
                                {stats.sent} versendet
                              </span>
                            )}
                          </div>
                          
                          <span className="text-xs text-slate-400 font-medium whitespace-nowrap">
                            {isExpanded ? 'Einklappen' : 'Ausklappen'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                }

                // Handle regular submissions
                const daysUntil = getDaysUntilDeadline(submission.deadline);
                const isSelected = selectedSubmissions.includes(submission._id);
                
                // Check if this submission belongs to a collapsed group
                // Look back to find the group header this submission belongs to
                let belongsToExpandedGroup = false;
                for (let i = index - 1; i >= 0; i--) {
                  const prevItem = submissions[i];
                  if (prevItem.isGroupHeader) {
                    belongsToExpandedGroup = expandedGroups.has(prevItem._id);
                    break;
                  }
                }
                
                // Don't render if belongs to collapsed group
                if (!belongsToExpandedGroup) {
                  return null;
                }
                
                return (
                  <motion.div
                    key={submission._id}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.01 }}
                    className={`grid grid-cols-[40px_minmax(0,1fr)_184px] gap-3 items-center px-4 py-3.5 transition-colors duration-150 text-sm ${
                      isSelected ? 'bg-red-50/70' : 'hover:bg-slate-50/80'
                    } ${
                      submission.deletedAt ? 'opacity-60' : ''
                    }`}
                  >
                    {/* **RUNDE CHECKBOX GANZ LINKS** */}
                    <div className="flex justify-start">
                      <div 
                        className="relative cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectSubmission(submission._id);
                        }}
                      >
                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                          isSelected
                            ? 'bg-red-500 border-red-500'
                            : 'border-slate-300 hover:border-red-400'
                        }`}>
                          {isSelected && (
                            <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* **KLICKBARER CONTENT BEREICH** */}
                    <div 
                      className="grid grid-cols-[minmax(150px,2.2fr)_minmax(140px,1.9fr)_minmax(104px,1.1fr)_minmax(128px,1.3fr)_minmax(150px,1.7fr)] gap-4 items-center cursor-pointer min-w-0"
                      onClick={() => handleShowDetail(submission._id)}
                    >
                      {/* **LIEFERANT** */}
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                          <span className="truncate">{submission.supplierId?.name || 'Unbekannt'}</span>
                          {submission.deletedAt && (
                            <span className="shrink-0 px-1.5 py-0.5 text-[10px] bg-red-100 text-red-700 rounded font-medium">
                              Gelöscht
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 truncate mt-0.5">
                          {submission.supplierId?.email}
                        </div>
                      </div>

                      {/* **TEMPLATE** */}
                      <div className="min-w-0">
                        <div className="text-slate-700 text-xs leading-snug line-clamp-2">
                          {submission.questionnaireId?.title?.de || 'Unbekannt'}
                        </div>
                      </div>

                      {/* **STATUS** */}
                      <div className="min-w-0">
                        {getStatusBadge(submission.status)}
                      </div>

                      {/* **DEADLINE** */}
                      <div className="min-w-0">
                        <div className="text-slate-900 font-medium text-xs whitespace-nowrap">
                          {formatDate(submission.deadline)}
                        </div>
                        <div className={`mt-1 inline-flex items-center px-1.5 py-0.5 rounded-md text-[11px] font-medium whitespace-nowrap ${
                          daysUntil < 0 ? 'bg-red-50 text-red-600' :
                          daysUntil <= 2 ? 'bg-orange-50 text-orange-600' : 'bg-emerald-50 text-emerald-600'
                        }`}>
                          {daysUntil < 0 
                            ? `${Math.abs(daysUntil)}d überfällig`
                            : daysUntil === 0
                              ? 'Heute fällig'
                              : `${daysUntil}d übrig`
                          }
                        </div>
                      </div>

                      {/* **FORTSCHRITT** */}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-200/80 rounded-full h-2 min-w-[48px] overflow-hidden">
                            <div 
                              className={`h-2 rounded-full transition-all duration-300 ${
                                submission.progressPercentage >= 80 ? 'bg-emerald-500' :
                                submission.progressPercentage >= 50 ? 'bg-yellow-500' :
                                submission.progressPercentage >= 20 ? 'bg-orange-500' : 'bg-red-500'
                              }`}
                              style={{ width: `${submission.progressPercentage}%` }}
                            ></div>
                          </div>
                          <span className="text-xs font-semibold text-slate-700 w-9 text-right tabular-nums">
                            {submission.progressPercentage}%
                          </span>
                        </div>
                        <div className="text-[11px] text-slate-400 mt-1 truncate">
                          {submission.progressPercentage >= 80 ? 'Fast fertig' :
                           submission.progressPercentage >= 50 ? 'In Bearbeitung' :
                           submission.progressPercentage >= 20 ? 'Begonnen' : 'Nicht begonnen'}
                        </div>
                      </div>
                    </div>

                    {/* **AKTIONEN (Icon-Buttons)** */}
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowDetail(submission._id);
                        }}
                        className="w-8 h-8 flex items-center justify-center text-xs bg-red-50 text-red-700 rounded-lg hover:bg-red-100 font-medium transition-colors"
                        title="Details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>

                      {canShowAnswers(submission) && (
                        <button
                          onClick={(e) => handleViewAnswers(submission, e)}
                          className="w-8 h-8 flex items-center justify-center text-xs bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 font-medium transition-colors"
                          title="Antworten anzeigen"
                        >
                          <FontAwesomeIcon icon={faClipboardList} />
                        </button>
                      )}
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowDeadlineModal(submission);
                        }}
                        className="w-8 h-8 flex items-center justify-center text-xs bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 font-medium transition-colors"
                        title="Deadline ändern"
                      >
                        <FontAwesomeIcon icon={faCalendarAlt} />
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSingleReminder(submission);
                        }}
                        disabled={submission.status === 'completed'}
                        className="w-8 h-8 flex items-center justify-center text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 font-medium transition-colors disabled:opacity-50"
                        title="Erinnerung senden"
                      >
                        <FontAwesomeIcon icon={faEnvelope} />
                      </button>
                      
                      <button
                        onClick={(e) => handleDeleteSubmission(submission._id, submission.supplierId?.name || 'Unbekannt', e)}
                        className="w-8 h-8 flex items-center justify-center text-xs bg-slate-100 text-slate-600 rounded-lg hover:bg-red-50 hover:text-red-700 font-medium transition-colors"
                        title="Löschen"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            </div>
            </div>
          </div>
        )}
      </div>

      {/* **PAGINATION** */}
      {totalPages > 1 && (
        <div className="max-w-[1700px] mx-auto px-4 sm:px-6 pb-4">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/70 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="text-sm text-slate-600">
                Seite {currentPage} von {totalPages} ({totalItems} Einträge)
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50 transition-colors"
                >
                  <FontAwesomeIcon icon={faChevronLeft} className="text-xs" />
                </button>
                
                {Array.from({length: Math.min(5, totalPages)}, (_, i) => {
                  const pageNum = Math.max(1, currentPage - 2) + i;
                  if (pageNum > totalPages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`w-8 h-8 flex items-center justify-center text-sm rounded-lg font-medium transition-colors ${
                        currentPage === pageNum
                          ? 'bg-gradient-to-br from-[#e2313a] to-[#c9252d] text-white shadow-sm'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 disabled:opacity-50 transition-colors"
                >
                  <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* **MODALS** */}
      {showBulkSendModal && (
        <BulkSendModal
          templates={templates}
          onClose={() => setShowBulkSendModal(false)}
          onSuccess={() => {
            setShowBulkSendModal(false);
            refreshSubmissions();
          }}
          onError={(message: string) => {
            setError(message);
          }}
        />
      )}
      
      {showDetailModal && selectedSubmissionId && (
        <SubmissionDetailModal
          submissionId={selectedSubmissionId}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedSubmissionId(null);
          }}
          onUpdate={refreshSubmissions}
        />
      )}
      
      {showDeadlineModal && deadlineModalData && (
        <DeadlineUpdateModal
          submissionId={deadlineModalData.submissionId}
          currentDeadline={deadlineModalData.currentDeadline}
          supplierName={deadlineModalData.supplierName}
          onClose={() => {
            setShowDeadlineModal(false);
            setDeadlineModalData(null);
          }}
          onUpdate={refreshSubmissions}
        />
      )}

      {showAnswersModal && selectedSubmissionForAnswers && (
        <SubmissionAnswersModal
          submissionId={selectedSubmissionForAnswers}
          onClose={handleCloseAnswersModal}
        />
      )}

      {/* **DELETE MODAL** */}
      {showDeleteModal && deleteModalData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-xl"
          >
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center mr-3">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900">
                {(() => {
                  const { title } = getDeleteModalContent();
                  return title;
                })()}
              </h3>
            </div>
            
            <p className="text-slate-600 mb-6">
              {(() => {
                const { message } = getDeleteModalContent();
                return message;
              })()}
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCloseDeleteModal}
                disabled={deleteLoading}
                className="px-4 py-2 text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-50 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
                className="px-4 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 flex items-center transition-colors"
              >
                {deleteLoading && (
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                )}
                Löschen
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Reminder Warning Modal */}
      <ReminderWarningModal
        isOpen={showReminderWarningModal}
        onClose={() => {
          setShowReminderWarningModal(false);
          setReminderWarningData(null);
        }}
        onConfirm={handleConfirmedReminder}
        supplierName={reminderWarningData?.submission.supplierId.name || ''}
        daysSinceLastReminder={reminderWarningData?.daysSinceLastReminder || 0}
        lastReminderDate={reminderWarningData?.lastReminderDate || new Date()}
      />
    </div>
  );
};

export default Submissions;